import { Skeleton } from '@mui/material';
import type { IReportMatrixResult } from '__generated__';
import { memo } from 'react';
import style from './index.module.scss';

// The report's fixed wording — the Word file prints the same text, so no label keys (R-35).
export const REPORT_MATRIX_HEAD = {
  category: 'קטגוריה',
  parameter: 'פרמטרים',
  measurement: 'מדידה',
  grade: 'ניקוד',
  derivedRating: 'דירוג נגזר',
  finalRating: 'דירוג בפועל',
} as const;

const SKELETON_ROWS = 6;

/** Numbers and grades are LTR text inside RTL cells. */
const Ltr = ({ value }: { value: string | null | undefined }) => <bdi dir="ltr">{value ?? ''}</bdi>;

type ReportMatrixTableProps = {
  data: IReportMatrixResult | null;
  isLoading: boolean;
  finalEmptyLabel: string;
};

export const ReportMatrixTable = memo(({ data, isLoading, finalEmptyLabel }: ReportMatrixTableProps) => {
  const rows = data?.rows ?? [];
  const finalRating = data?.finalRating ?? '';

  return (
    <div className={style.scroll} dir="rtl">
      <table className={style.table}>
        <thead>
          <tr>
            <th rowSpan={2} className={style.colCategory}>
              {REPORT_MATRIX_HEAD.category}
            </th>
            <th rowSpan={2} className={style.colParameter}>
              {REPORT_MATRIX_HEAD.parameter}
            </th>
            <th colSpan={2} className={style.groupHead}>
              {isLoading ? <Skeleton width="60%" sx={{ mx: 'auto' }} /> : <Ltr value={data?.actualColumnTitle} />}
            </th>
            <th colSpan={2} className={style.groupHead}>
              {isLoading ? <Skeleton width="60%" sx={{ mx: 'auto' }} /> : <Ltr value={data?.forecastColumnTitle} />}
            </th>
          </tr>
          <tr>
            <th className={style.valueCol}>{REPORT_MATRIX_HEAD.measurement}</th>
            <th className={style.valueCol}>{REPORT_MATRIX_HEAD.grade}</th>
            <th className={style.valueCol}>{REPORT_MATRIX_HEAD.measurement}</th>
            <th className={style.valueCol}>{REPORT_MATRIX_HEAD.grade}</th>
          </tr>
        </thead>

        <tbody>
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={index}>
                  {Array.from({ length: 6 }, (__, cell) => (
                    <td key={cell}>
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row, index) => (
                <tr key={index}>
                  {row.categoryRowSpan > 0 ? (
                    <td rowSpan={row.categoryRowSpan} className={style.categoryCell}>
                      {row.category}
                    </td>
                  ) : null}
                  <td>{row.parameter}</td>
                  <td className={style.valueCell}>
                    <Ltr value={row.actualMeasurement} />
                  </td>
                  <td className={style.valueCell}>
                    <Ltr value={row.actualGrade} />
                  </td>
                  <td className={style.valueCell}>
                    <Ltr value={row.forecastMeasurement} />
                  </td>
                  <td className={style.valueCell}>
                    <Ltr value={row.forecastGrade} />
                  </td>
                </tr>
              ))}
        </tbody>

        {!isLoading && data ? (
          <tfoot>
            <tr>
              <th colSpan={2} className={style.footLabel}>
                {REPORT_MATRIX_HEAD.derivedRating}
              </th>
              <td />
              <td />
              <td />
              <td className={`${style.valueCell} ${style.footValue}`}>
                <Ltr value={data.derivedRating} />
              </td>
            </tr>
            <tr>
              <th colSpan={2} className={style.footLabel}>
                {REPORT_MATRIX_HEAD.finalRating}
              </th>
              <td />
              <td />
              <td />
              {finalRating ? (
                <td className={`${style.valueCell} ${style.footValue}`}>
                  <Ltr value={finalRating} />
                </td>
              ) : (
                <td className={`${style.valueCell} ${style.footEmpty}`}>{finalEmptyLabel}</td>
              )}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
});

ReportMatrixTable.displayName = 'ReportMatrixTable';
